import {
  CheckOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Select, Space } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import React, { useEffect, useState } from 'react';
import { Grid, Integration } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';

export function IntegrationDetailsForm(props: { integration?: Integration; onSaved?: () => void }) {
  const { Option } = Select;

  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columns, setColumns] = useState<string[]>();
  const [urlEditable, setUrlEditable] = useState(false);
  const [grids, setGrids] = useState<Grid[]>();
  const [gridsLoading, setGridsLoading] = useState(false);

  useEffect(() => {
    if (!props.integration) {
      return;
    }
    if (props.integration.integrationType === 'IMPORT') {
      if (props.integration.sourceColumns?.length) {
        const filteredColumns = filterNestedColumns(props.integration.sourceColumns);
        setColumns(filteredColumns);
      }

      let cacheColumns: string[] = [];
      apiClient
        .get(`integrations/${props.integration.id}/columns`)
        .then(response => {
          if (!response.data) {
            return;
          }
          cacheColumns = response.data?.map((m: any) => m.name) as string[];
          cacheColumns = cacheColumns?.sort((m1, m2) => (m1 < m2 ? -1 : 1));

          const filteredColumns = filterNestedColumns(cacheColumns);
          setColumns(filteredColumns);
        })
        .catch(() => {
          if (!cacheColumns) {
            message.error('Kaynak veriye ulaşılamadı. Lütfen bağlantı bilgilerini kontrol edin.');
          }
        });
    } else {
      setGridsLoading(true);
      apiClient
        .get('grids')
        .then(response => {
          setGrids(response.data as Grid[]);
          setGridsLoading(false);
        })
        .catch((reason: any) => {
          setGridsLoading(false);
          apiErrorMessage(reason);
        });
    }
  }, [props.integration]);

  const filterNestedColumns = (columns: string[]): string[] => {
    return columns.filter(col => {
      // Check if this column is part of a nested structure
      const isNested = col.includes('.');
      if (isNested) {
        return false;
      }
      // Check if this column has any subcolumns
      const hasSubcolumns = columns.some(c => c !== col && c.startsWith(col + '.'));
      return !hasSubcolumns;
    });
  };

  const onValuesChange = (changedValues: any) => {
    if (changedValues.url) {
      setColumns(undefined);
      form.resetFields(['identifierColumns']);
      form.setFieldsValue({ identifierColumns: undefined });
    }
  };

  const loadColumns = () => {
    if (columnsLoading) {
      return;
    }
    form
      .validateFields(['url'])
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        if (form.getFieldError('url').length) {
          return;
        }
        setColumnsLoading(true);
        const values = form.getFieldsValue();
        const body = {
          url: values.url,
          headers: values.headers,
        };
        apiClient
          .post('integrations/columns', body)
          .then(response => {
            if (response.data.length) {
              const filteredColumns = filterNestedColumns(response.data);
              setColumns(filteredColumns);
            } else {
              form.setFieldsValue({ identifierColumns: undefined });
              setColumns(undefined);
              message.error('Kaynak veri boş. Lütfen kontrol edin.');
            }
          })
          .catch(() => {
            form.setFieldsValue({ identifierColumns: undefined });
            setColumns(undefined);
            message.error('Kaynak veriye ulaşılamadı. Lütfen bağlantı bilgilerini kontrol edin.');
          })
          .finally(() => {
            setColumnsLoading(false);
          });
      });
  };

  const onUrlLockClick = () => {
    if (urlEditable) {
      setUrlEditable(false);
    } else {
      const onOk = () => setUrlEditable(true);
      Modal.confirm({
        title: 'URL düzenle',
        icon: <ExclamationCircleOutlined />,
        content: 'Entegrasyon bağlantı adresini değiştirmek istediğinize emin misiniz?',
        okText: 'Evet',
        cancelText: 'Vazgeç',
        onOk: onOk,
      });
    }
  };

  const confirmUrlUsage = (values: any) => {
    const onOk = () => submitForm(values);
    Modal.confirm({
      title: 'URL',
      icon: <ExclamationCircleOutlined />,
      content: 'Girdiğiniz URL daha önce başka bir entegrasyonda kullanılmış, devam etmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const submitForm = (values: any) => {
    setLoading(true);
    apiClient
      .put(`integrations/${props.integration?.id}`, values)
      .then(() => {
        setLoading(false);
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const onEditFinish = (values: any) => {
    setLoading(true);
    if (props.integration?.url === values.url) {
      submitForm(values);
    } else {
      apiClient
        .get('integrations/check?url=' + values.url)
        .then((response: any) => {
          setLoading(false);
          if (response.data === true) {
            confirmUrlUsage(values);
          } else {
            submitForm(values);
          }
        })
        .catch((reason: any) => {
          setLoading(false);
          apiErrorMessage(reason);
        });
    }
  };

  return (
    <Form layout="vertical" form={form} onFinish={onEditFinish} initialValues={props?.integration} onValuesChange={onValuesChange}>
      <Form.Item label="Tür">
        <span className="ant-form-text" style={{ display: 'inline-block', marginLeft: '1rem' }}>
          {props.integration?.integrationType === 'IMPORT' ? 'İçe entegrasyon' : 'Dışa entegrasyon'}
        </span>
      </Form.Item>
      <Form.Item
        label="İsim"
        name="name"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
            message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
          },
          { max: 100, message: 'En fazla 100 karakter' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        label="Açıklama"
        name="description"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          { max: 4000, message: 'En fazla 4000 karakter' },
        ]}
      >
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        label={
          <>
            <span>URL</span>
            {urlEditable ? (
              <UnlockOutlined style={{ marginLeft: '0.5rem' }} onClick={onUrlLockClick} />
            ) : (
              <LockOutlined style={{ marginLeft: '0.5rem' }} onClick={onUrlLockClick} />
            )}
          </>
        }
        name="url"
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /https?:\/\/((localhost:\d{4})|(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6})\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)/,
            message: 'Geçersiz URL',
          },
          { max: 500, message: 'En fazla 500 karakter' },
        ]}
      >
        <Input disabled={!urlEditable} />
      </Form.Item>
      <Form.List name="headers">
        {(fields, { add, remove }) => (
          <>
            {fields.map((field, index) => (
              <Form.Item label={index === 0 ? 'Başlıklar' : ''} required={false} key={field.key} style={{ marginBottom: 0 }}>
                <Space key={field.key} align="baseline">
                  <Form.Item {...field} name={[field.name, 'name']} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                    <Input placeholder="İsim" />
                  </Form.Item>
                  <Form.Item {...field} name={[field.name, 'value']} rules={[{ required: true, message: 'Zorunlu alan' }]}>
                    <Input placeholder="Değer" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              </Form.Item>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>
                İstek başlığı ekle
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
      {props.integration?.integrationType === 'IMPORT' && (
        <>
          <Form.Item label="Bağlantı testi" required={true}>
            <Button loading={columnsLoading} onClick={loadColumns} icon={columns && columns?.length > 0 ? <CheckOutlined /> : null}>
              {columns && columns.length > 0 ? 'Bağlantı başarılı' : 'Şimdi dene'}
            </Button>
          </Form.Item>
          <Form.Item label="Tanım veri elemanları" name="identifierColumns" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <Select mode="multiple" allowClear>
              {columns?.map(c => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
      {props.integration?.integrationType === 'EXPORT' && (
        <>
          <Form.Item label="Ana veri seti" name="primaryGridId" rules={[{ required: true, message: 'Zorunlu alan' }]}>
            <Select loading={gridsLoading}>
              {grids?.map(g => (
                <Option key={g.id} value={g.id}>
                  {g.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </>
      )}
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={props.integration?.integrationType !== 'EXPORT' && columns === undefined}
        >
          Kaydet
        </Button>
      </Form.Item>
    </Form>
  );
}
